import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { api } from '../../utils/api';

type TplRow = { key: string; code: string; name: string; category: 'client' | 'internal'; description: string; enabled: number; updated_at: number; updated_by_name: string };
type TplVar = { name: string; desc: string; sample: string; raw?: boolean };
type TplFull = TplRow & { subject: string; body_html: string; vars: TplVar[]; default: { subject: string; body: string } | null };

// Live preview: naive {{var}} → sample-value replacement (display only).
function fillSample(s: string, vars: TplVar[]): string {
  return String(s || '').replace(/\{\{\s*([a-zA-Z0-9_]+)\s*\}\}/g, (_m, n) => {
    const v = vars.find(x => x.name === n); return v ? v.sample : '';
  });
}

export default function EmailTemplates() {
  const { t } = useTranslation();
  const [rows, setRows] = useState<TplRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [sel, setSel] = useState<string | null>(null);
  const [full, setFull] = useState<TplFull | null>(null);
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [enabled, setEnabled] = useState(true);
  const [saving, setSaving] = useState(false);
  const [busy, setBusy] = useState('');
  const [msg, setMsg] = useState<{ kind: 'ok' | 'err'; text: string } | null>(null);

  const load = () => { setLoading(true); api.adminGetEmailTemplates().then((r: any) => setRows(r.rows || [])).finally(() => setLoading(false)); };
  useEffect(load, []);

  const open = async (key: string) => {
    setSel(key); setMsg(null); setFull(null);
    const f: any = await api.adminGetEmailTemplate(key);
    setFull(f); setSubject(f.subject || ''); setBody(f.body_html || ''); setEnabled(!!f.enabled);
  };
  const dirty = full && (subject !== full.subject || body !== full.body_html || enabled !== !!full.enabled);

  const save = async () => {
    if (!sel) return; setSaving(true); setMsg(null);
    try { await api.adminSaveEmailTemplate(sel, { subject, body, enabled }); setMsg({ kind: 'ok', text: t('et_saved') }); await open(sel); load(); }
    catch (e: any) { setMsg({ kind: 'err', text: e.message }); } finally { setSaving(false); }
  };
  const restore = async () => {
    if (!sel || !window.confirm(t('et_restore_confirm'))) return;
    setBusy('restore'); setMsg(null);
    try { const r: any = await api.adminRestoreEmailTemplate(sel); setSubject(r.subject); setBody(r.body); setMsg({ kind: 'ok', text: t('et_restored') }); load(); }
    catch (e: any) { setMsg({ kind: 'err', text: e.message }); } finally { setBusy(''); }
  };
  const test = async () => {
    if (!sel) return; setBusy('test'); setMsg(null);
    try { const r: any = await api.adminTestEmailTemplate(sel); setMsg({ kind: 'ok', text: `${t('et_test_sent')} ${r.sentTo}` }); }
    catch (e: any) { setMsg({ kind: 'err', text: e.message }); } finally { setBusy(''); }
  };
  const insertVar = (name: string) => setBody(b => `${b}{{${name}}}`);

  const groups: { cat: 'client' | 'internal'; label: string }[] = [
    { cat: 'client', label: t('et_cat_client') }, { cat: 'internal', label: t('et_cat_internal') },
  ];

  const inp: React.CSSProperties = { width: '100%', padding: '9px 11px', fontSize: 13, border: '1px solid var(--border-strong)', background: 'var(--bg-app)', borderRadius: 8, color: 'var(--text-primary)', outline: 'none', boxSizing: 'border-box' };
  const lbl: React.CSSProperties = { fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.03em', color: 'var(--text-secondary)', marginBottom: 5, display: 'block' };
  const btn = (bg: string, c: string): React.CSSProperties => ({ padding: '8px 16px', borderRadius: 8, border: 'none', background: bg, color: c, fontWeight: 700, fontSize: 12.5, cursor: 'pointer' });

  return (
    <div style={{ display: 'flex', gap: 16, alignItems: 'flex-start' }}>
      {/* ── Template list ── */}
      <div style={{ width: 320, flexShrink: 0, background: 'var(--bg-surface)', borderRadius: 'var(--radius-md)', border: '1px solid var(--border)', overflow: 'hidden' }}>
        {loading ? <div style={{ padding: 24, textAlign: 'center', color: 'var(--text-tertiary)' }}><i className="ti ti-loader" /></div> : groups.map(g => {
          const items = rows.filter(r => r.category === g.cat);
          if (!items.length) return null;
          return (
            <div key={g.cat}>
              <div style={{ padding: '8px 14px', fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.04em', color: 'var(--text-tertiary)', background: 'var(--bg-app)', borderBottom: '1px solid var(--border)' }}>{g.label}</div>
              {items.map(r => (
                <button key={r.key} onClick={() => open(r.key)} style={{ display: 'block', width: '100%', textAlign: 'left', padding: '10px 14px', border: 'none', borderBottom: '1px solid var(--border)', background: sel === r.key ? 'var(--bg-active)' : 'transparent', cursor: 'pointer' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <span style={{ fontFamily: 'monospace', fontSize: 11, fontWeight: 700, color: 'var(--accent)', background: 'var(--bg-active)', padding: '1px 6px', borderRadius: 5 }}>{r.code}</span>
                    <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-primary)' }}>{r.name}</span>
                    {!r.enabled && <span style={{ marginLeft: 'auto', fontSize: 10, fontWeight: 700, color: '#B45309', background: '#FEF3C7', padding: '1px 6px', borderRadius: 5 }}>OFF</span>}
                  </div>
                  <div style={{ fontSize: 11.5, color: 'var(--text-tertiary)', marginTop: 3 }}>{r.description}</div>
                </button>
              ))}
            </div>
          );
        })}
      </div>

      {/* ── Editor ── */}
      <div style={{ flex: 1, minWidth: 0 }}>
        {!full ? (
          <div style={{ padding: 40, textAlign: 'center', color: 'var(--text-tertiary)', fontSize: 13 }}>{t('et_pick')}</div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <span style={{ fontFamily: 'monospace', fontSize: 12, fontWeight: 700, color: 'var(--accent)', background: 'var(--bg-active)', padding: '2px 8px', borderRadius: 6 }}>{full.code}</span>
              <span style={{ fontSize: 16, fontWeight: 800, color: 'var(--text-primary)' }}>{full.name}</span>
              <label style={{ marginLeft: 'auto', fontSize: 12.5, color: 'var(--text-secondary)', display: 'inline-flex', alignItems: 'center', gap: 6, cursor: 'pointer' }}>
                <input type="checkbox" checked={enabled} onChange={e => setEnabled(e.target.checked)} /> {t('et_enabled')}
              </label>
            </div>

            <div>
              <label style={lbl}>{t('et_subject')}</label>
              <input value={subject} onChange={e => setSubject(e.target.value)} style={inp} />
            </div>

            {/* Variable chips */}
            <div>
              <label style={lbl}>{t('et_variables')}</label>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                {full.vars.map(v => (
                  <button key={v.name} onClick={() => insertVar(v.name)} title={`${v.desc}${v.raw ? ' (HTML)' : ''}`}
                    style={{ fontFamily: 'monospace', fontSize: 11.5, fontWeight: 600, padding: '3px 8px', borderRadius: 6, border: '1px solid var(--border-strong)', background: 'var(--bg-app)', color: v.raw ? '#7C3AED' : 'var(--text-secondary)', cursor: 'pointer' }}>
                    {`{{${v.name}}}`}
                  </button>
                ))}
              </div>
            </div>

            <div style={{ display: 'flex', gap: 14, alignItems: 'flex-start' }}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <label style={lbl}>{t('et_body')}</label>
                <textarea value={body} onChange={e => setBody(e.target.value)} rows={18}
                  style={{ ...inp, fontFamily: 'monospace', fontSize: 12.5, lineHeight: 1.5, resize: 'vertical' }} />
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <label style={lbl}>{t('et_preview')}</label>
                <div style={{ border: '1px solid var(--border-strong)', borderRadius: 8, background: '#fff', padding: '12px 14px', minHeight: 200, maxHeight: 420, overflow: 'auto' }}>
                  <div style={{ fontSize: 13, fontWeight: 700, color: '#111', borderBottom: '1px solid #eee', paddingBottom: 8, marginBottom: 10 }}>{fillSample(subject, full.vars) || '—'}</div>
                  <div style={{ fontSize: 13, color: '#222' }} dangerouslySetInnerHTML={{ __html: fillSample(body, full.vars) }} />
                </div>
              </div>
            </div>

            {msg && <div style={{ fontSize: 12.5, padding: '8px 12px', borderRadius: 8, background: msg.kind === 'ok' ? '#ECFDF3' : '#FCEBEB', color: msg.kind === 'ok' ? '#067647' : '#E24B4A' }}>{msg.text}</div>}

            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              <button onClick={save} disabled={saving || !dirty} style={{ ...btn('var(--accent)', '#fff'), opacity: saving || !dirty ? 0.6 : 1 }}>{saving ? '…' : t('save')}</button>
              <button onClick={test} disabled={!!busy} style={btn('var(--bg-active)', 'var(--accent)')}>{busy === 'test' ? '…' : t('et_send_test')}</button>
              <div style={{ flex: 1 }} />
              {full.default && <button onClick={restore} disabled={!!busy} style={btn('var(--c-gray-100)', 'var(--text-secondary)')}>{busy === 'restore' ? '…' : t('et_restore')}</button>}
            </div>
            <div style={{ fontSize: 11.5, color: 'var(--text-tertiary)' }}>{t('et_test_hint')}</div>
          </div>
        )}
      </div>
    </div>
  );
}
